/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { AxiosError } from 'axios';
import { chunk } from 'lodash';
import type { MosaicNode } from 'react-mosaic-component';
import { getLeaves } from 'react-mosaic-component';
import { FEATURE_GROUP_PREFIX, syncGroupsTypes } from '@opengeoweb/store';
import type {
  WorkspacePreset,
  WorkspacePresetForBE,
  WorkspaceViewsFromBE,
  ViewType,
  WorkspaceViewPresetComponentsLookupType,
  WorkspaceSyncGroup,
  WorkspaceViewType,
  WorkspaceViews,
} from './types';

interface ErrorData {
  message: string;
}

export const getViewPresetState = (
  viewPresetId: string,
  mosaicNodeId: string,
  state: 'loading' | 'error',
): {
  title: string;
  id: string;
  mosaicNodeId: string;
  componentType: WorkspaceViewPresetComponentsLookupType;
} => {
  const titlePrefix = state === 'loading' ? 'Loading' : 'Error';
  const componentType = state === 'loading' ? 'ViewLoading' : 'ViewError';

  return {
    title: `${titlePrefix} ${viewPresetId}`,
    id: viewPresetId,
    mosaicNodeId,
    componentType,
  };
};

export const getLoadingWorkspace = (
  workspacePreset: WorkspacePresetForBE,
): WorkspacePreset => {
  const views = workspacePreset.views.reduce(
    (object, viewDetails) => {
      // If the view has a linked preset → use loading placeholder
      if (viewDetails.viewPresetId) {
        // eslint-disable-next-line no-param-reassign
        object.byId[viewDetails.mosaicNodeId] = getViewPresetState(
          viewDetails.viewPresetId,
          viewDetails.mosaicNodeId,
          'loading',
        );
      } else {
        // Inline view — hydrate it directly
        // eslint-disable-next-line no-param-reassign
        object.byId[viewDetails.mosaicNodeId] = {
          id: viewDetails.mosaicNodeId,
          mosaicNodeId: viewDetails.mosaicNodeId,
          title: viewDetails.title,
          componentType: viewDetails.componentType ?? '',
          initialProps: viewDetails.initialProps ?? {},
        };
      }

      object.allIds.push(viewDetails.mosaicNodeId);
      return object;
    },
    { allIds: [] as string[], byId: {} as Record<string, WorkspaceViewType> },
  );

  return {
    ...workspacePreset,
    views,
  };
};

const BATCH_SIZE = 3;
export const getListInChunks = (
  views: WorkspaceViewsFromBE[],
): WorkspaceViewsFromBE[][] => chunk(views, BATCH_SIZE);

export const defaultSyncGroups: syncGroupsTypes.SyncGroupAddGroupPayload[] =
  syncGroupsTypes.SyncGroupTypeList.map((syncgroupType) => ({
    groupId: syncgroupType,
    title: `Default group for ${syncgroupType}`,
    type: syncgroupType as syncGroupsTypes.SyncType,
  }));

export const getSyncGroups = (
  syncGroups: WorkspaceSyncGroup[] = [],
): syncGroupsTypes.SyncGroupAddGroupPayload[] => {
  const presetSyncGroups: syncGroupsTypes.SyncGroupAddGroupPayload[] =
    syncGroups.map((syncGroup) => ({
      groupId: syncGroup.id,
      title: syncGroup.title ?? syncGroup.id,
      type: syncGroup.type,
      sourcePanelId:
        syncGroup.sourcePanelId ||
        (syncGroup.type ===
          ('SYNCGROUPS_TYPE_SETFEATURES' as syncGroupsTypes.SyncType) &&
        syncGroup.id.startsWith(FEATURE_GROUP_PREFIX)
          ? syncGroup.id.slice(FEATURE_GROUP_PREFIX.length)
          : undefined),
    }));

  //  Only add a default sync group for a type that is not used yet
  defaultSyncGroups.forEach(
    (defaultSyncGroup) =>
      presetSyncGroups.findIndex(
        (presetSyncGroup) => presetSyncGroup.type === defaultSyncGroup.type,
      ) === -1 && presetSyncGroups.push(defaultSyncGroup),
  );

  return presetSyncGroups;
};

export const tiledWindowComponentTypes = [
  'MultiMap',
  'ModelRunInterval',
  'HarmonieTempAndPrecipPreset',
];

export const getViewType = (
  numViews: number,
  firstViewComponentType: string,
): ViewType => {
  // If more than one view set multiWindow
  if (numViews > 1) {
    return 'multiWindow';
  }
  if (numViews === 1) {
    // set tiledWindow if multimap
    return tiledWindowComponentTypes.includes(firstViewComponentType)
      ? 'tiledWindow'
      : 'singleWindow';
  }
  // no views
  return undefined!;
};

/**
 * Get all view ids from the mosaicnode state
 * @param mNode MosaicNode state
 * @returns List if string ids of all mosaic nodes.
 */
export const makeListOfViewIds = (mNode: MosaicNode<string>): string[] => {
  return getLeaves(mNode);
};

export const getErrorMessage = (error: AxiosError<unknown>): string => {
  const prefix = error.message;
  const suffix = (error.response?.data as ErrorData)?.message;

  return suffix ? `${prefix}: ${suffix}` : prefix;
};

export const toViewsArray = (
  wsViews: WorkspaceViewsFromBE[] | WorkspaceViews | null | undefined,
): WorkspaceViewsFromBE[] => {
  if (!wsViews) {
    return [];
  }

  if (Array.isArray(wsViews)) {
    return wsViews;
  }

  const { allIds, byId } = wsViews;

  if (!allIds || !byId) {
    return [];
  }

  return allIds.map((id) => {
    const view = byId[id] ?? {};

    return {
      mosaicNodeId: id,
      title: view.title,
      componentType: view.componentType,
      initialProps: view.initialProps,
      viewPresetId: (view as WorkspaceViewsFromBE).viewPresetId,
      originalViewPresetId: (view as WorkspaceViewsFromBE).originalViewPresetId,
    };
  });
};
